import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, RefreshCw, X, TrendingUp } from 'lucide-react';
import { format, subDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PostKeywordsModal = ({ isOpen, onClose, post, token, configId }) => {
  const [loading, setLoading] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [dateRange, setDateRange] = useState('28');
  
  const dateRangeOptions = [
    { value: '7', label: 'Last 7 days' },
    { value: '28', label: 'Last 28 days' },
    { value: '90', label: 'Last 3 months' }
  ];

  const fetchKeywords = async () => {
    if (!token || !post) return;
    
    setLoading(true);
    try {
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');
      
      // Use the post slug as the page URL filter
      const pageUrl = post.slug || post.link?.split('/').filter(Boolean).pop() || '';
      
      const response = await axios.post(
        `${API}/gsc/page-keywords`,
        {
          page_url: pageUrl,
          start_date: startDate,
          end_date: endDate,
          config_id: configId,
          limit: 50
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setKeywords(response.data.keywords || []);
    } catch (error) {
      console.error('Fetch keywords error:', error);
      toast.error('Failed to load keywords');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && post) {
      fetchKeywords();
    }
  }, [isOpen, post, dateRange]);

  const formatNumber = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const getPositionColor = (position) => {
    if (position <= 3) return 'text-green-400';
    if (position <= 10) return 'text-yellow-400';
    if (position <= 20) return 'text-orange-400';
    return 'text-red-400';
  };

  const getPositionBadge = (position) => {
    if (position <= 3) return 'bg-green-500/20 text-green-400';
    if (position <= 10) return 'bg-yellow-500/20 text-yellow-400';
    if (position <= 20) return 'bg-orange-500/20 text-orange-400';
    return 'bg-red-500/20 text-red-400';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-white/20 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Ranking Keywords</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Post Info */}
        <div className="px-4 py-3 bg-white/5 border-b border-white/10">
          <h3 className="text-white font-medium line-clamp-1">{post?.title || 'Post'}</h3>
          <p className="text-xs text-slate-400 mt-1">/{post?.slug || ''}</p>
        </div>

        {/* Date Range Selector */}
        <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-md px-3 py-1.5 text-sm text-white"
          >
            {dateRangeOptions.map(opt => (
              <option key={opt.value} value={opt.value} className="bg-slate-800">
                {opt.label}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchKeywords}
            disabled={loading}
            className="border-white/20 text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Keywords List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : keywords.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No keywords found</p>
              <p className="text-sm mt-1">This post may not have ranking data yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-blue-400">
                    {formatNumber(keywords.reduce((sum, k) => sum + k.clicks, 0))}
                  </p>
                  <p className="text-xs text-slate-400">Total Clicks</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-purple-400">
                    {formatNumber(keywords.reduce((sum, k) => sum + k.impressions, 0))}
                  </p>
                  <p className="text-xs text-slate-400">Impressions</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-400">{keywords.length}</p>
                  <p className="text-xs text-slate-400">Keywords</p>
                </div>
              </div>

              {/* Keywords Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 border-b border-white/10">
                      <th className="text-left py-2 font-medium">Keyword</th>
                      <th className="text-center py-2 font-medium">Position</th>
                      <th className="text-right py-2 font-medium">Clicks</th>
                      <th className="text-right py-2 font-medium">Impr.</th>
                      <th className="text-right py-2 font-medium">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.map((kw, idx) => (
                      <tr key={idx} className="border-b border-white/5 hover:bg-white/5 text-white">
                        <td className="py-2 pr-4 max-w-xs">
                          <span className="line-clamp-2" title={kw.query}>
                            {kw.query}
                          </span>
                        </td>
                        <td className="py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getPositionBadge(kw.position)}`}>
                            #{kw.position}
                          </span>
                        </td>
                        <td className="text-right py-2 text-blue-400 font-medium">{formatNumber(kw.clicks)}</td>
                        <td className="text-right py-2 text-slate-400">{formatNumber(kw.impressions)}</td>
                        <td className="text-right py-2 text-green-400">{kw.ctr}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-white/10 bg-white/5">
          <p className="text-xs text-slate-500 text-center">
            Data from Google Search Console • Use these keywords to optimize your content
          </p>
        </div>
      </div>
    </div>
  );
};

export default PostKeywordsModal;
